import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import { API_CARD_DEFS_URL, API_COLLECTIONS_URL } from '../utils';
import { listGlobalCollections } from '../services/publication';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { normalizeId } from '../utils/ids';

const SEARCH_MAX = 5;

/**
 * The cards a collection's lookup may point at IN GLOBAL COLLECTIONS
 * (docs/plans/personal-discover-global.md §4.5, ruling e07: any published
 * one, followed or not). The ones the caller follows are offered at once;
 * any other is found by `search(text)`. The cards land in the card cache too
 * (addCards), so a chosen target renders everywhere afterwards.
 *
 *   { cards: [card & { globalCollection: { id, title } }], publishedIds: Set, search, searching }
 *
 * `enabled` false (a global card's form, a new card with no collection yet)
 * costs nothing.
 */
export default function useGlobalLookupTargets({ enabled = true, excludeCollectionId = null, keepIds = [], knownIds = null } = {}) {
	const cache = useOptionalCardDefinitions();
	const addCards = cache?.addCards;
	const [collections, setCollections] = useState([]);
	const [loaded, setLoaded] = useState({}); // collectionId -> cards
	const [searching, setSearching] = useState(false);

	const loadCards = useCallback(async rows => {
		const fresh = {};
		for (const row of rows) {
			const id = normalizeId(row._id);
			try {
				const { data } = await api.get(`${API_COLLECTIONS_URL}/${id}/cards`);
				const title = row.publication?.listing?.title || row.name;
				fresh[id] = (Array.isArray(data) ? data : [])
					.filter(card => card?.scope === 'collection' && normalizeId(card.collection_id) === id)
					.map(card => ({ ...card, globalCollection: { id, title } }));
			} catch {
				fresh[id] = [];
			}
		}
		setLoaded(prev => ({ ...prev, ...fresh }));
		const all = Object.values(fresh).flat();
		if (all.length && typeof addCards === 'function') addCards(all.map(({ globalCollection, ...card }) => card));
	}, [addCards]);

	useEffect(() => {
		if (!enabled) return undefined;
		let live = true;
		listGlobalCollections({ limit: 100 })
			.then(({ rows }) => {
				if (!live) return;
				const published = rows.filter(row => row.kind !== 'archive' && row.publication?.state === 'published' && normalizeId(row._id) !== normalizeId(excludeCollectionId));
				setCollections(published);
				loadCards(published.filter(row => row.followed));
			})
			.catch(() => { if (live) setCollections([]); });
		return () => { live = false; };
	}, [enabled, excludeCollectionId, loadCards]);

	// A target an element is ALREADY saved with, in a collection that is not
	// loaded above (not followed, or no longer published): read singly so the
	// picker shows its name, not a bare id.
	const [kept, setKept] = useState([]);
	const keepKey = (keepIds || []).map(normalizeId).filter(Boolean).sort().join(',');
	useEffect(() => {
		if (!enabled || !keepKey) return undefined;
		let live = true;
		const have = new Set([...Object.values(loaded).flat().map(c => normalizeId(c._id)), ...(knownIds ? [...knownIds] : [])]);
		const wanted = keepKey.split(',').filter(id => !have.has(id));
		if (!wanted.length) return undefined;
		Promise.all(wanted.map(id => api.get(`${API_CARD_DEFS_URL}/${id}`).then(({ data }) => data).catch(() => null)))
			.then(docs => {
				if (!live) return;
				const foreign = docs.filter(card => card && card.scope === 'collection' && normalizeId(card.collection_id) !== normalizeId(excludeCollectionId));
				const titleOf = cid => {
					const row = collections.find(r => normalizeId(r._id) === cid);
					return row ? (row.publication?.listing?.title || row.name) : 'another collection';
				};
				setKept(foreign.map(card => ({ ...card, globalCollection: { id: normalizeId(card.collection_id), title: titleOf(normalizeId(card.collection_id)) } })));
			});
		return () => { live = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, keepKey, excludeCollectionId, collections]);

	/** Load the cards of the global collections whose listing matches `text`. */
	const search = useCallback(async text => {
		const q = (text || '').trim().toLowerCase();
		if (!q) return;
		setSearching(true);
		try {
			const matches = collections
				.filter(row => !loaded[normalizeId(row._id)])
				.filter(row => [row.name, row.publication?.listing?.title, row.publication?.listing?.summary].some(v => (v || '').toLowerCase().includes(q)))
				.slice(0, SEARCH_MAX);
			if (matches.length) await loadCards(matches);
		} finally {
			setSearching(false);
		}
	}, [collections, loaded, loadCards]);

	const publishedIds = useMemo(() => new Set(collections.map(row => normalizeId(row._id))), [collections]);
	const cards = useMemo(() => {
		const list = Object.values(loaded).flat();
		const ids = new Set(list.map(c => normalizeId(c._id)));
		return [...list, ...kept.filter(c => !ids.has(normalizeId(c._id)))];
	}, [loaded, kept]);
	return { cards, publishedIds, search, searching, collections };
}
